<script lang="ts" setup>
import { onBeforeUnmount, type PropType, type Ref, ref, watchEffect } from 'vue';
import { ContainerTypes, getContainer, getContext, isCaseWithError } from '@dandelion/core';
import type { CaseListActionInterface } from '../../interfaces/CaseListActionInterface';
import { CaseListRowStyleInterface } from '@dandelion/core';
import type { CaseListServiceInterface } from '@dandelion/core';
import type { CaseListColumn } from '@dandelion/core';
import type TranslationService from '@dandelion/core/src/services/TranslationService';
import { NamedBindings } from '@dandelion/core';
import { ApplicationEvents } from '@dandelion/core';
import { OpenCaseRequested } from '@dandelion/core';

const props = defineProps({
  rowIndex: {
    type: Number as PropType<number>,
    required: true,
  },
  caseListColumns: {
    type: Array as PropType<Array<CaseListColumn>>,
    required: false,
    default: () => new Array<CaseListColumn>(),
  },
  caseListRowStyles: {
    type: Array as PropType<Array<CaseListRowStyleInterface>>,
    required: false,
    default: () => new Array<CaseListRowStyleInterface>(),
  },
  context: {
    type: String as PropType<string>,
    required: true,
  },
  actions: {
    type: Array as PropType<Array<CaseListActionInterface>>,
    required: false,
    default: () => [],
  },
  openOnLineClick: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false,
  },
  isDeletedCaseList: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false,
  },
});

const container = getContainer(props.context);
const caseListService = container.get(NamedBindings.CaseListService) as CaseListServiceInterface;
const translationService = container.get(NamedBindings.TranslationService) as TranslationService;

const rowClasses: Ref<Array<string>> = ref([]);
const htmlContents: Ref<{ [key: string]: string }> = ref({});
const referenceList = props.isDeletedCaseList ? caseListService.slicedDeletedCases : caseListService.slicedCases;
const row = referenceList[props.rowIndex]; //pass row as a prop could break reactivity

const classesOnInitialization = isCaseWithError(row)
  ? []
  : props.caseListRowStyles.map((style) => style.applyStyle(row));

function applyDynamicClasses() {
  classesOnInitialization.forEach((rowStyle) => {
    Object.entries(rowStyle).forEach(([className, isActive]) => {
      if (isActive.value && !rowClasses.value.includes(className)) {
        rowClasses.value.push(className);
      } else if (!isActive.value && rowClasses.value.includes(className)) {
        rowClasses.value = rowClasses.value.filter((cls) => cls !== className);
      }
    });
  });
}

async function updateContent() {
  if (isCaseWithError(row)) {
    return;
  }
  for (const c of props.caseListColumns) {

    htmlContents.value[c.getHeading()] = c.getContentAsHTML(row);  
      if(c.getHeading().toLocaleLowerCase() == "consent")
        htmlContents.value[c.getHeading()] = `<span class='row-value variable-value ${c.getHeading()
      .toLocaleLowerCase()
      .replaceAll(/\s+/g, '-')}-variable-value'>${translationService.t(row.cache.get("consent"))}</span>`
  }
}

watchEffect(() => {
  applyDynamicClasses();
  updateContent();
});

//TODO: check
onBeforeUnmount(() => { }); //necessary for caseListElement reactivity after creating a case

function openCase(caseID: string) {
  getContext(props.context).dispatchEvent(
    ApplicationEvents.OpenCaseRequested,
    new OpenCaseRequested(ContainerTypes.DEFAULT, caseID)
  );
}
const errorcheck:string = "doesn't exist"
</script>

<template>
  <tr v-if="isCaseWithError(row) && !row.error.endsWith(errorcheck)" class="'case-list-row-with-error'">
    <td :colspan="caseListColumns.length + 1">
      {{ row.caseID }} - {{ translationService.t('Case is temporary locked by another user') }}
      <!-- {{ row.error }} -->
    </td>
  </tr>
  <tr
    v-else-if="row.error == undefined || !row.error.endsWith(errorcheck)"
    @click="openOnLineClick ? openCase(row.caseMetaData.caseID) : null"
    :class="[...rowClasses, { 'line-with-action': openOnLineClick }, 'case-list-row']"
  >
    <td
      v-for="c in caseListColumns"
      :key="c.getHeading()"
      :class="[`${c.getHeading().toLocaleLowerCase().replace(/\s+/g, '-')}-cell`, 'row-cell']"
      v-html="htmlContents[c.getHeading()]"
    />

    <td class="actions-cell row-cell">
      <section class="action-buttons-wrapper">
        <template v-for="action in actions" :key="action.name">
          <button
            type="button"
            v-if="!action.isVisible || action.isVisible({ element: row, elementIndex: rowIndex, rowProps: props })"
            :class="[`${action.actionClass}-btn`, 'row-action-btn','hoverable-row-icon']"
            @click.stop="action.action({ element: row, elementIndex: rowIndex, rowProps: props })"
          >            
          <template v-if="action.icon">
              <ion-icon :name="action.icon"></ion-icon>
              <span class="tooltip" v-html="translationService.t(action.tooltip || '')" />
            </template>
            <span v-if="!action.icon" v-html="translationService.t(action.label || action.name)" />
          </button>
        </template>
      </section>
    </td>
  </tr>
</template>

<style>
.row-icon-wrapper {
  display: flex;
  justify-content: center;
}

.hoverable-row-icon ion-icon {
  font-size: 1.5rem;
}

.hoverable-row-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.hoverable-row-icon .tooltip {
  position: absolute;
  width: fit-content;
  white-space: nowrap;
  visibility: hidden;
  background-color: #555;
  color: #fff;
  text-align: center;
  padding: 0.5rem 0.8rem;
  border-radius: 0.5rem;
  bottom: 125%;
  opacity: 0;
  transition: opacity 0.3s;
  z-index:999;
}

.hoverable-row-icon .tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #555 transparent transparent transparent;
}

.hoverable-row-icon:hover .tooltip {
  visibility: visible;
  opacity: 1;
}
.action-buttons-wrapper{
  display: flex;
}
</style>
